import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';
import { ReactComponent as BackIcon } from 'assets/icons/chevron_left.svg';
import { usePaginatedIconSearch } from 'hooks/usePaginatedIconSearch';
import { UserType } from 'modules/User';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconColorOption } from 'types/icon.types';
import { NewWidget } from 'widgets/Widget.types';

import { IconList } from '../IconList';
import { PartialIconMetadata } from '../IconWidgetMenu.types';
import { IconPackData } from './IconView.types';

/**
 * ICON PACK COMPONENT
 *
 * Renders a paginated list of icons that belong to a specific icon pack.
 * Icons are retrieved from Algolia using the usePaginatedIconSearch hook.
 */
interface IconPackProps {
  iconPackData: IconPackData | null;
  onExitView: () => void;
  onClickIconWidget: (iconId: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => void;
  onOpenModal: () => void;
  createWidgetData: (iconId: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => NewWidget;
  colorOptions?: IconColorOption[];
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  disableDragDrop?: boolean;
}

export const IconPack = ({
  iconPackData,
  onExitView,
  onClickIconWidget,
  colorOptions,
  setUpgradeCallback,
  onOpenModal,
  createWidgetData,
  disableDragDrop,
}: IconPackProps) => {
  // Set filter by pack flag when doing paginated search
  const { hasMore, isLoadingMore, icons, onShowMore, searchIcons, clearSearch } = usePaginatedIconSearch({
    isIconPack: true,
    filterByIconPack: !iconPackData?.isIcons8,
    filterByIcons8Platform: !!iconPackData?.isIcons8,
    colorOptions,
  });

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });

  // Update icon list when selected pack changes
  useEffect(() => {
    if (!iconPackData) {
      clearSearch();
      return;
    }

    if (iconPackData.isIcons8) {
      const { category, platform } = iconPackData;
      const searchString = [category, platform].join(',');
      searchIcons(searchString);
    } else {
      searchIcons(iconPackData.id.toString());
    }
  }, [iconPackData, searchIcons, clearSearch]);

  const onCreateWidgetData = (iconId: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => {
    return createWidgetData(iconId, viewBox, mono || iconPackData?.isMono, iconMetadata);
  };

  const handleClickIconWidget = (
    iconId: string,
    viewBox: string,
    mono?: boolean,
    iconMetadata?: PartialIconMetadata,
  ) => {
    onClickIconWidget(iconId, viewBox, mono || iconPackData?.isMono, iconMetadata);
  };

  if (!iconPackData) {
    return null;
  }

  return (
    <Flex overflow={'auto'}>
      <Flex w={'full'} p={3} pr={0} overflow={'hidden'} direction={'column'}>
        <Flex alignItems={'center'} h={30}>
          <IconButton
            variant={'icon-btn-side-menu'}
            size={'xs'}
            icon={<BackIcon />}
            aria-label={'Go back to icon pack list'}
            onClick={onExitView}
            autoFocus={true}
          />
          <Text as='h2' ml={2} fontSize='sm' color='font.500' fontWeight='semibold'>
            {t(`mockIconPack.${iconPackData.id.toString()}` as keyof typeof t)}
          </Text>
        </Flex>

        <Box overflow={'auto'} flex={1} position={'relative'}>
          <Box mt={4}>
            <IconList
              icons={icons}
              onClickIconWidget={handleClickIconWidget}
              testId={'iconwidgetmenu-pack-icon-list'}
              setUpgradeCallback={setUpgradeCallback}
              onOpenModal={onOpenModal}
              createWidgetData={onCreateWidgetData}
              disableDragDrop={disableDragDrop}
            />
          </Box>

          {/* Show more button */}
          {hasMore && (
            <Flex justify='center'>
              <Button isLoading={isLoadingMore} onClick={onShowMore} colorScheme='green' mt={4} size='sm'>
                {t('showMore')}
              </Button>
            </Flex>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
